import type { ElasticSearchOptions } from '@comfama/frontend-utils';

/**
 * ============================================================================
 *  CONFIGURACIÓN DEL BUSCADOR UNIVERSAL
 * ============================================================================
 *
 * Este es el ÚNICO archivo que un desarrollador debería editar para adaptar la
 * feature a otro proyecto, índice o entorno. Toda la lógica (hooks, vistas,
 * servicios y utilidades) lee desde aquí, de modo que no hay valores "mágicos"
 * repartidos por el código.
 *
 * Los campos que dependen de variables de entorno usan `get` (getters) para
 * leerse de forma perezosa: así respetan el `process.env` vigente en runtime y
 * en tests, no el del momento en que se importa el módulo.
 */
export const searchConfig = {
    /**
     * Conexión y consulta a Elasticsearch para el buscador y las sugerencias.
     */
    elastic: {
        /** URL base del clúster (variable de entorno). */
        get baseUrl(): string {
            return process.env.ELASTICSEARCH_URL ?? '';
        },
        /** API key opcional para el endpoint de búsqueda. */
        get apiKey(): string {
            return process.env.ELASTICSEARCH_API_KEY ?? '';
        },
        /**
         * Índices sobre los que se busca. Acepta la sintaxis de Elasticsearch
         * (`*` = todos, `-foo` = excluir `foo`).
         */
        indexQuery: '*,-cda_contentful_projects_ai',
        boostIndex: ['portal_servicios_contentful_logstash'],
        selectedFields: [
            'id',
            'origin',
            'category',
            'title',
            'description',
            'tags',
            'centrosCulturales',
            'municipality',
        ],
    },

    /**
     * Contenido destacado del header (top content): más buscados, contenido
     * para ti y hot words.
     */
    topContent: {
        /** API key del endpoint de top content (variable de entorno). */
        get apiKey(): string {
            return process.env.ELASTICSEARCH_TOP_CONTENT_API_KEY ?? '';
        },
        /** Ruta del documento de top content relativa a `elastic.baseUrl`. */
        docPath: 'search_top_content/_doc/top_content_portal_servicios',
    },

    /** Ruta de la página de resultados a la que navega el buscador del header. */
    resultsRoute: '/resultados-busqueda/',

    /**
     * Índice meta que se genera en build (`gatsby-node`). Es un JSON con una
     * entrada por cada tarjeta de servicio del Home ("Home portal de
     * servicios", `ContentfulInfoCard`), la misma fuente que consume
     * `public/metas-mf/meta-contentful-{Empresa,Persona}.json` (ver
     * `loadIndex`/`loadPages` en `gatsby-node.ts`), consultada de forma
     * independiente para no acoplarse a ese estado en memoria. Toda la lógica
     * de generación vive en `build/metaIndex.ts` dentro de esta misma feature.
     */
    metaIndex: {
        /** Carpeta de salida (relativa a `public/`). */
        outDir: 'meta',
        /** Nombre del archivo JSON generado. */
        fileName: 'meta-portal-servicios.json',
        /**
         * Ancho (px) de la imagen procesada por `gatsby-plugin-image` que se
         * solicita en la query. Debe coincidir con el que usan las páginas que
         * consumen `ContentfulInfoCard` (p. ej. `useProfileData.tsx`,
         * `useEnterpriseProfileData.tsx`) para apuntar al MISMO archivo ya
         * generado en el build y no procesar otro derivado.
         */
        imageWidth: {
            homeServicio: 700,
        },
    },

    /** Búsquedas recientes guardadas en `localStorage`. */
    recentSearches: {
        storageKey: 'recent_searches',
        /** Máximo de términos recientes que se conservan. */
        maxItems: 2,
        /** Ícono que acompaña cada término reciente. */
        icon: 'icon-Search',
    },
    filterMapping: {
        categorías: 'tags',
    } as Record<string, string>,

    /** Breakpoint (px) por debajo del cual se considera viewport mobile. */
    mobileBreakpoint: 640,

    /**
     * Metadatos SEO de la página de resultados (`src/pages/resultados-busqueda.tsx`).
     *
     * `title` es el título completo: `SEO` no antepone marca (ver `src/shared/seo.tsx`).
     * El canonical no se declara aquí a propósito: al no recibir `urlCanonical`,
     * `SEO` lo deriva de la URL actual.
     *
     * `noIndex` va en true porque es una página de resultados parametrizada
     * (`?q=`), que no aporta nada al índice de los buscadores.
     */
    seo: {
        title: 'Portal de servicios | Resultados de búsqueda',
        description:
            'Encuentra fácilmente lo que necesitas en el Portal de Servicios de Comfama.',
        noIndex: true,
    },
};

/**
 * Opciones listas para `useElasticSearch`, derivadas de `searchConfig`.
 * Centraliza la construcción de la URL de búsqueda para que el header y la
 * vista de resultados compartan exactamente la misma configuración.
 */
export const getElasticOptions = (): ElasticSearchOptions => ({
    apikeyElastic: searchConfig.elastic.apiKey,
    urlElastic: `${searchConfig.elastic.baseUrl}/_search?index=${searchConfig.elastic.indexQuery}`,
    selectedFields: searchConfig.elastic.selectedFields,
    boostIndex: searchConfig.elastic.boostIndex,
});
